import { ReactElement, useCallback } from "react";
import * as CSS from "csstype";
import {
  Box,
  HStack,
  Spacer,
  Stack,
  StackDirection,
  Text,
} from "@chakra-ui/react";
import { useSearchParams } from "react-router-dom";

import {
  QuotationLineModelDocumentModelContractStatusEnum,
  QuotationModel,
} from "api";
import Page from "components/Page";
import { DesktopOnly } from "components/BreakpointVisibility";
import RequestParamsProvider, {
  ActiveSort,
  useRequestParams,
} from "contexts/RequestParamsProvider";
import useQueryParam, { QUERY_STRING_KEYS } from "hooks/useQueryParam";
import useContracts from "hooks/api/contracts/useContracts";
import { SORT_DIRECTION } from "hooks/useSort";
import { RequestContractButton } from "routes/RequestContractPage";
import { dropShadowCardSize } from "theme/foundations/shadows";
import { mobileStyleBreakpoint } from "util/styleHelpers";
import ContractDetailsDrawer from "./details/ContractDetailsDrawer";
import ContractsList from "./list/ContractsList";
import ContractFilters from "./list/ContractFilters";
import ContractListSort, {
  CONTRACT_LIST_SORT_KEYS,
} from "./list/ContractListSort";

const listViewCountTextHeight = "24px"; // Fixed height to list doesn't jump after load

const contractListFlexDirection =
  mobileStyleBreakpoint<CSS.Property.FlexDirection>("column", "row");

const contractListFilterSpacing = mobileStyleBreakpoint<number>(3, 8);

const filtersPadding = mobileStyleBreakpoint<string | number>(
  0,
  dropShadowCardSize
);

const customerSelectWidth = mobileStyleBreakpoint<CSS.Property.Width>(
  "100%",
  "unset"
);
const ContractsPage = (): ReactElement => {
  const [, setSearchParams] = useSearchParams();
  const { searchTerm, activeSort, filters } = useRequestParams();

  const [selectedContractDocEntry, setSelectedContractDocEntry] = useQueryParam(
    QUERY_STRING_KEYS.SELECTED_CONTRACT_KEY
  );

  const { data: contracts } = useContracts(
    searchTerm,
    filters as ContractListFilters,
    activeSort as ActiveSort<QuotationModel>
  );

  const onSelectContract = useCallback(
    (docEntry: number) =>
      setSelectedContractDocEntry(
        contracts?.find((_contract) => _contract.docEntry === docEntry)
          ?.docEntry
      ),
    [contracts, setSelectedContractDocEntry]
  );

  const onClearContract = useCallback(() => {
    setSearchParams((searchParams) => {
      const nextSearchParams = new URLSearchParams(searchParams);
      nextSearchParams.delete(QUERY_STRING_KEYS.SELECTED_CONTRACT_KEY);
      nextSearchParams.delete(QUERY_STRING_KEYS.SELECTED_TAB_KEY);
      nextSearchParams.delete(QUERY_STRING_KEYS.FORM_OPEN);
      return nextSearchParams;
    });
  }, [setSearchParams]);

  return (
    <>
      <Stack spacing={8}>
        <DesktopOnly>
          <HStack>
            <Spacer />
            <RequestContractButton />
          </HStack>
        </DesktopOnly>
        <Stack
          w="100%"
          align="flex-start"
          spacing={contractListFilterSpacing}
          direction={contractListFlexDirection as StackDirection}
          pt={4}
        >
          <Box px={filtersPadding} w={customerSelectWidth}>
            <ContractFilters contracts={contracts} />
          </Box>
          <Stack flex={3} width="100%" height="100%" spacing={3}>
            <DesktopOnly>
              <Stack
                justify="space-between"
                align="flex-end"
                direction="row"
                height={listViewCountTextHeight}
              >
                {(contracts?.length ?? 0) > 0 && (
                  <Text
                    fontWeight="semibold"
                    color="grey.500"
                  >{`Viewing ${contracts?.length} contracts`}</Text>
                )}
                <Spacer flex={1} />
                <Box>
                  <ContractListSort />
                </Box>
              </Stack>
            </DesktopOnly>
            <Stack overflowY="auto" overflowX="hidden" pb={6}>
              <ContractsList
                contracts={contracts}
                onViewDetails={onSelectContract}
              />
            </Stack>
          </Stack>
        </Stack>
      </Stack>
      <ContractDetailsDrawer
        isOpen={!!selectedContractDocEntry}
        onClose={onClearContract}
      />
    </>
  );
};

const FilterableContractsPage = (): ReactElement => (
  <RequestParamsProvider
    initialFilters={{
      statuses: [
        QuotationLineModelDocumentModelContractStatusEnum.Request,
        QuotationLineModelDocumentModelContractStatusEnum.Offer,
        QuotationLineModelDocumentModelContractStatusEnum.Open,
      ],
    }}
    initialSort={{
      key: CONTRACT_LIST_SORT_KEYS.CONTRACT_STATUS,
      direction: SORT_DIRECTION.DESC,
    }}
  >
    <Page>
      <ContractsPage />
    </Page>
  </RequestParamsProvider>
);

export default FilterableContractsPage;
